import type { OnboardingTasks } from '@paintscout/util/builder'

/**
 * The onboarding checklist in the resource center (#25354): the four `options.onboardingTasks` flags
 * the API sets on a company's first quote, deal, event and production event (`handleSaveQuote.ts`,
 * `put-deal.ts`, `put-event.ts`). Read only: nothing here writes `$options`.
 */

/** Sent as `item` on `checklist_item_clicked`. Never rename a value once shipped. */
export type ChecklistItemId = 'quote' | 'deal' | 'event' | 'production_event'

export interface ChecklistItem {
  id: ChecklistItemId
  label: string
  /** The page that completes the item. No tour in `TOUR_REGISTRY` covers these tasks yet. */
  path: string
  done: boolean
}

export interface ChecklistAccess {
  /** Quotes are on for the company and the user may create one (as the New button decides). */
  quote: boolean
  /** CRM on and visible, and the user may create a deal. */
  deal: boolean
  /** CRM on and visible, and the user may create an event. */
  event: boolean
  /** As `event`; the production calendar exists only with the `crm` flag. */
  productionEvent: boolean
}

export interface ChecklistInput {
  tasks: OnboardingTasks | undefined
  access: ChecklistAccess
  /** The company's word for a quote (`getObjectLabels().quote.value`), e.g. "Estimate". */
  quoteLabel: string
}

/** The items this user can complete, in order. An item the user cannot do is left out. */
export function getChecklistItems({ tasks, access, quoteLabel }: ChecklistInput): ChecklistItem[] {
  const items: ChecklistItem[] = []
  if (access.quote) {
    items.push({
      id: 'quote',
      label: `Create your first ${(quoteLabel || 'quote').toLowerCase()}`,
      path: '/quotes/',
      done: !!tasks?.hasCreatedQuote
    })
  }
  if (access.deal) {
    items.push({
      id: 'deal',
      label: 'Add a deal to your pipeline',
      path: '/deals/board/',
      done: !!tasks?.hasCreatedDeal
    })
  }
  if (access.event) {
    items.push({
      id: 'event',
      label: 'Book an event on your calendar',
      path: '/events/?tab=calendar',
      done: !!tasks?.hasCreatedEvent
    })
  }
  if (access.productionEvent) {
    items.push({
      id: 'production_event',
      label: 'Schedule a job on the production calendar',
      path: '/scheduling/?tab=calendar',
      done: !!tasks?.hasCreatedProductionEvent
    })
  }
  return items
}

/** True when there is nothing left to show: every item is done, or the user can do none. */
export function isChecklistComplete(items: ChecklistItem[]): boolean {
  return items.every((item) => item.done)
}

/**
 * The app loads options once and nothing re-fetches them, so the checklist refreshes them itself when
 * the panel opens with an item left. appDocs carries all of options and rates, so at most once a minute.
 */
export const OPTIONS_REFRESH_INTERVAL_MS = 60_000

export function shouldRefreshOptions(lastRefreshAt: number | null, now: number): boolean {
  return lastRefreshAt === null || now - lastRefreshAt >= OPTIONS_REFRESH_INTERVAL_MS
}

// Last refresh per company. Keyed by company, so a different company signed in on the same tab is
// not held back by the previous one's throttle.
const lastRefreshByCompany = new Map<string, number>()

/** Claims the refresh for `companyId` when it is due. Returns false inside the throttle window. */
export function claimOptionsRefresh(companyId: string, now: number): boolean {
  if (!shouldRefreshOptions(lastRefreshByCompany.get(companyId) ?? null, now)) return false
  lastRefreshByCompany.set(companyId, now)
  return true
}

/** Gives the claim back after a failed refresh, so the next open tries again. */
export function releaseOptionsRefresh(companyId: string): void {
  lastRefreshByCompany.delete(companyId)
}

/** Test-only. */
export function resetOptionsRefreshForTests(): void {
  lastRefreshByCompany.clear()
}
